import { useQuery } from '@tanstack/react-query'
import { barterGroupApi } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import { formatDate } from '@/utils/format'
import { Users, MessageSquare, Shield } from 'lucide-react'
import type { BarterGroup } from '@/types'

export default function BarterGroupList() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['barter-groups'],
    queryFn: () => barterGroupApi.list(),
  })

  const groups = data?.data?.groups || []
  const isAdmin = user?.user_type === 'super_admin' || user?.user_type === 'admin'

  if (isLoading) return <Loading />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">
            {isAdmin ? 'All Barter Groups' : 'My Barter Groups'}
          </h1>
          {isAdmin && (
            <p className="text-sm text-gray-600 mt-1">
              <Shield className="w-4 h-4 inline mr-1" />
              Admin view - You can see all barter groups in the system
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group: BarterGroup) => (
          <Card
            key={group.id}
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(`/dashboard/groups/${group.id}`)}
          >
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Group #{group.id}</h3>
                  <p className="text-sm text-gray-500">{formatDate(group.created_at)}</p>
                </div>
                <Badge status={group.status}>
                  {group.status.replace('_', ' ')}
                </Badge>
              </div>

              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  <span>{group.member_count || 0} members</span>
                </div>
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  <span>{group.message_count || 0} messages</span>
                </div>
              </div>

              {group.members && group.members.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">Members:</p>
                  <div className="flex flex-wrap gap-2">
                    {group.members.slice(0, 3).map((member: any) => (
                      <div
                        key={member.user_id}
                        className="text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        {member.user_name}
                      </div>
                    ))}
                    {group.members.length > 3 && (
                      <div className="text-xs text-gray-500 px-2 py-1">
                        +{group.members.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/dashboard/groups/${group.id}`)
                }}
              >
                View Details
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {groups.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {isAdmin ? 'No barter groups exist in the system yet' : 'You are not part of any barter groups yet'}
            </p>
            <p className="text-sm text-gray-400">
              Barter groups are created automatically when multiple users agree to a multi-way barter
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}
